
import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { User, Client, PayPeriod, PayPeriodStatus, TimeEntry } from '../types';
import { ArrowLeft, Clock, Plus, ChevronRight, CheckCircle2, Edit2, X } from 'lucide-react';

interface ClientDetailProps {
  user: User;
  clientId: string;
  onBack: () => void;
  onEnterTime: (periodId?: string) => void;
}

const ClientDetail: React.FC<ClientDetailProps> = ({ user, clientId, onBack, onEnterTime }) => {
  const [client, setClient] = useState<Client | null>(null);
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [unbilledEntries, setUnbilledEntries] = useState<TimeEntry[]>([]);
  
  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', rate: 0 });

  useEffect(() => {
    refreshData();
  }, [user.id, clientId]);

  const refreshData = () => {
    const c = db.getClients(user.id).find(c => c.id === clientId);
    if (c) {
      setClient(c);
      setFormData({ name: c.name, email: c.email || '', rate: c.defaultRate });
      setPeriods(db.getPayPeriods(user.id).filter(p => p.clientId === clientId));
      setUnbilledEntries(db.getOpenEntriesForClient(user.id, clientId));
    }
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    
    const updated: Client = {
      ...client,
      name: formData.name,
      email: formData.email || undefined,
      defaultRate: formData.rate
    };
    
    db.saveClient(updated);
    setClient(updated);
    setShowEditModal(false);
    refreshData();
  };

  if (!client) return null;

  const totalUnbilledHrs = unbilledEntries.reduce((sum, e) => sum + e.hoursCalculated, 0);
  const totalUnbilledAmt = unbilledEntries.reduce((sum, e) => sum + e.amountCalculated, 0);
  const lifetimePaidAmt = periods
    .filter(p => p.status === PayPeriodStatus.PAID)
    .reduce((sum, p) => sum + p.totalAmount, 0);

  const getStatusStyle = (status: PayPeriodStatus) => {
    switch (status) {
      case PayPeriodStatus.OPEN: return 'bg-blue-50 text-blue-700';
      case PayPeriodStatus.SENT: return 'bg-amber-50 text-amber-700';
      case PayPeriodStatus.PAID: return 'bg-green-50 text-green-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const formatCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-slate-800 transition font-black text-[10px] uppercase tracking-widest py-1">
        <ArrowLeft size={14} strokeWidth={3} /> Back to Clients
      </button>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="flex flex-col">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-none">{client.name}</h1>
            <button 
              onClick={() => setShowEditModal(true)}
              className="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
              title="Edit Client Info"
            >
              <Edit2 size={18} />
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-2 font-medium">
            {client.email || 'No billing email set'} • <span className="text-blue-600 font-black">{formatCurrency(client.defaultRate)}/hr</span>
          </p>
        </div>
        <button 
          onClick={() => onEnterTime()}
          className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition shadow-lg shadow-blue-500/10 active:scale-95"
        >
          <Plus size={18} strokeWidth={3} /> Log New Hours
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Unbilled Stats Card */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
              <Clock size={12} className="text-blue-600" /> Pending Balance
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-slate-500 font-bold">Unbilled Hours:</span>
                <span className="text-xl font-black text-slate-900">{totalUnbilledHrs.toFixed(2)}h</span>
              </div>
              <div className="flex justify-between items-baseline pt-2 border-t border-slate-50">
                <span className="text-xs text-slate-500 font-bold">Amount Due:</span>
                <span className="text-xl font-black text-blue-600">{formatCurrency(totalUnbilledAmt)}</span>
              </div>
              <div className="flex justify-between items-baseline pt-4 border-t border-slate-50 border-dashed">
                <div className="flex items-center gap-1.5 text-xs text-slate-400 font-bold uppercase tracking-wider">
                  <CheckCircle2 size={10} className="text-green-500" /> Lifetime Paid:
                </div>
                <span className="text-sm font-black text-slate-500">{formatCurrency(lifetimePaidAmt)}</span>
              </div>
            </div>
            <button 
              disabled={unbilledEntries.length === 0}
              onClick={() => onEnterTime()}
              className="w-full bg-slate-900 hover:bg-black text-white py-3 rounded-xl font-bold text-xs disabled:opacity-30 transition transition-colors uppercase tracking-widest active:scale-95"
            >
              Generate Invoice
            </button>
          </div>
        </div>

        {/* History Table Card */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-50 bg-slate-50/30 flex items-center justify-between">
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Invoicing History</h3>
            </div>
            <div className="overflow-x-auto scrollbar-hide">
              <table className="w-full text-left">
                <thead className="bg-slate-50/50 text-slate-400 text-[9px] uppercase font-black tracking-widest border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-3">Pay Period</th>
                    <th className="px-6 py-3">Hours</th>
                    <th className="px-6 py-3 text-right">Total</th>
                    <th className="px-6 py-3 text-center">Status</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {periods.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center text-slate-300 text-sm font-bold italic">No history recorded yet for this client.</td>
                    </tr>
                  ) : (
                    periods.map(period => (
                      <tr key={period.id} className="hover:bg-slate-50 transition group cursor-pointer" onClick={() => onEnterTime(period.id)}>
                        <td className="px-6 py-4">
                          <span className="text-sm font-bold text-slate-900">
                            {formatDateDisplay(period.startDate)} – {formatDateDisplay(period.endDate)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-500">{period.totalHours.toFixed(2)}h</td>
                        <td className="px-6 py-4 text-right font-black text-slate-900 text-sm">{formatCurrency(period.totalAmount)}</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${getStatusStyle(period.status)}`}>
                            {period.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ChevronRight size={14} className="text-slate-300 group-hover:text-blue-600 transition-all transform group-hover:translate-x-1 inline-block" />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl p-8 space-y-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-blue-600"></div>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Edit Client Details</h2>
              <button onClick={() => setShowEditModal(false)} className="text-slate-300 hover:text-slate-600 transition">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Client Name</label>
                <input required type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-sm font-bold focus:border-blue-500 transition" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Billing Contact Email</label>
                <input type="email" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-sm font-bold focus:border-blue-500 transition" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Default Hourly Rate ($)</label>
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 focus-within:border-blue-500 transition">
                  <span className="text-slate-400 font-bold">$</span>
                  <input required type="number" step="0.01" className="w-full py-3 bg-transparent outline-none text-sm font-bold" value={formData.rate} onChange={e => setFormData({...formData, rate: parseFloat(e.target.value) || 0})} />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 py-3 bg-white border border-slate-200 rounded-xl font-bold text-slate-600 text-xs uppercase tracking-widest hover:bg-slate-50 transition active:scale-95">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-blue-700 transition shadow-lg shadow-blue-500/10 active:scale-95">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
